<template>
    <Drawer ref="drawer" title="Edit OCR Invoice" :loading="isLoading || isUpdating" size="is-large" paddingless>
        <template #aside>
            <PdfViewer class="pdf-view" :url="doc.file_url" />
        </template>
        <Banner v-if="overspendAmount > 0" type="is-danger" class="!tw-rounded-none !tw-border-t-0 !tw-border-x-0">
            <div class="tw-flex tw-items-center tw-gap-4">
                <i class="mdi mdi-alert tw-text-base"></i>
                <span>
                    {{
                        selectedPurchaseOrders.length > 1
                            ? 'One or more of the selected purchase orders'
                            : 'The selected purchase order'
                    }}
                    would be overspent by {{ formatCurrency(overspendAmount, 2, targetCurrency) }} if this invoice were
                    created.
                </span>
            </div>
        </Banner>
        <Banner
            v-if="doc.is_potential_duplicate"
            type="is-warning"
            class="!tw-rounded-none !tw-border-t-0 !tw-border-x-0"
        >
            <div class="tw-flex tw-items-center tw-gap-4">
                <i class="mdi mdi-alert tw-text-yellow-500 tw-text-base"></i>
                <span>
                    This document may be a duplicate of a previously uploaded document, review before continuing.
                </span>
            </div>
        </Banner>
        <Banner v-if="hasBeenInvoiced" type="is-warning" class="!tw-rounded-none !tw-border-t-0 !tw-border-x-0">
            <div class="tw-flex tw-items-center tw-gap-4">
                <i class="mdi mdi-alert tw-text-yellow-500 tw-text-base"></i>
                <span>
                    {{
                        selectedPurchaseOrders.length > 1
                            ? 'One or more of the selected purchase orders'
                            : 'The selected purchase order'
                    }}
                    has already been invoiced, please review before continuing.
                </span>
            </div>
        </Banner>
        <div class="tw-px-6 tw-py-4">
            <PurchaseOrderSelector
                ref="purchaseOrderSelector"
                v-model="selectedPurchaseOrderId"
                :account-id="store.get('account.id')"
                :endpoint-query-params="{ status: ['Approved'], perPage: 7 }"
                multiple
                @updated="prepopulateFieldsFromPOSelection"
            />
        </div>

        <div class="tw-relative tw-border-t tw-px-6 tw-py-4 tw-flex-1 tw-grid tw-gap-3">
            <div class="tw-grid tw-gap-4 md:tw-grid-cols-7">
                <FormField label="Invoice Number" class="md:tw-col-span-2">
                    <Textbox v-model="doc.aiSupplierInvoice.invoice_number" />
                </FormField>
                <FormField label="Title" class="md:tw-col-span-5">
                    <Textbox v-model="doc.aiSupplierInvoice.title" />
                </FormField>
            </div>
            <div class="tw-grid tw-gap-4 md:tw-grid-cols-2">
                <FormField label="Invoice Date">
                    <VueFlatPickr
                        v-model="doc.aiSupplierInvoice.invoice_date"
                        placeholder="Required"
                        :config="flatpickrConfig"
                        class="input is-small"
                        required
                    />
                </FormField>
                <FormField label="Due Date">
                    <VueFlatPickr
                        v-model="doc.aiSupplierInvoice.due_date"
                        placeholder="Required"
                        :config="flatpickrConfig"
                        class="input is-small"
                        required
                    />
                </FormField>
            </div>

            <div class="tw-flex tw-gap-4 tw-mb-2">
                <FormField label="Target Currency" class="tw-flex-1">
                    <Select v-model="doc.aiSupplierInvoice.target_currency" placeholder="Select a currency">
                        <option disabled value="">Select a currency</option>
                        <option v-for="(label, code) in availableCurrencies" :key="code" :value="code">
                            {{ label }}
                        </option>
                    </Select>
                </FormField>
                <FormField label="Conversion Rate" field-class="tw-flex tw-gap-1">
                    <NumberInput
                        v-model="doc.aiSupplierInvoice.conversion_rate"
                        :disabled="doc.aiSupplierInvoice.base_currency == doc.aiSupplierInvoice.target_currency"
                        min="0"
                    />
                    <Button @click="openCurrencyConverter()"><i class="mdi mdi-calculator"></i></Button>
                </FormField>
            </div>

            <DataTable
                :columns="[
                    {
                        title: 'Description',
                        field: 'description',
                    },
                    {
                        title: 'Qty',
                        field: 'quantity',
                        width: 90,
                    },
                    {
                        title: 'Unit Price',
                        field: 'unit_price_target',
                        width: 160,
                    },
                    {
                        title: 'Tax',
                        field: 'tax_rate_id',
                    },
                    {
                        field: 'actions',
                        class: 'tw-text-right',
                    },
                ]"
                :data="doc.aiSupplierInvoice.line_items"
            >
                <template #column(description)="{ row }">
                    <Selector
                        v-model="row.purchase_order_line_item_id"
                        :options="availableLineItems"
                        :all-options="availableLineItems"
                        object-name-key="description"
                        @update:model-value="prepopulateFieldsFromPoliSelection(row)"
                    >
                        <template #empty>
                            <div class="tw-p-3 tw-text-gray-400">There are no line items available to select</div>
                        </template>
                    </Selector>
                </template>
                <template #column(quantity)="{ row }">
                    <NumberInput v-model="row.quantity" :min="1" />
                </template>
                <template #column(unit_price_target)="{ row }">
                    <NumberInput
                        v-model="row.unit_price_target"
                        step=".01"
                        :currency="
                            formatCurrency(
                                0,
                                0,
                                doc.aiSupplierInvoice.target_currency
                                    ? doc.aiSupplierInvoice.target_currency
                                    : useDataStore().get('account.currency_code')
                            ).replace('0', '')
                        "
                    />
                </template>
                <template #column(tax_rate_id)="{ row }">
                    <TaxRateSelector v-model="row.tax_rate_id" type="expense" compact />
                </template>
                <template #column(actions)="{ index }">
                    <Button type="is-danger" @click="removeLineItem(index)">
                        <i class="mdi mdi-trash-can"></i>
                    </Button>
                </template>
                <template #footer>
                    <tfoot>
                        <tr>
                            <th>
                                <Button type="is-link" class="!tw-p-0" @click="addLineItem">
                                    <i class="mdi mdi-plus"></i>
                                    Add line item
                                </Button>
                            </th>
                            <th class="tw-text-right" colspan="4">
                                <div class="tw-grid tw-grid-cols-[1fr,auto] tw-gap-3">
                                    <div class="tw-text-gray-400">Subtotal</div>
                                    <div>
                                        {{ formatCurrency(subtotal, 2, targetCurrency) }}
                                    </div>
                                    <div class="tw-text-gray-400">Tax</div>
                                    <div>
                                        {{ formatCurrency(taxAmount, 2, targetCurrency) }}
                                    </div>
                                    <div class="tw-text-gray-400">Total</div>
                                    <div>
                                        {{ formatCurrency(totalGross, 2, targetCurrency) }}
                                    </div>
                                </div>
                            </th>
                        </tr>
                    </tfoot>
                </template>
            </DataTable>
            <div
                v-if="!selectedPurchaseOrderId.length"
                class="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-white/70 tw-text-gray-400 tw-select-none"
            >
                You must select a purchase order in order to continue
            </div>
        </div>
        <template #footer>
            <div class="tw-flex tw-gap-3 tw-justify-end">
                <Button @click="save(true)">Save For Later</Button>

                <Button type="is-primary" :disabled="!isValid" @click="convertToSupplierOrder">
                    Create Supplier Invoice
                </Button>
            </div>
        </template>
    </Drawer>
</template>
<script setup>
import Drawer from '@/js/components/Drawer.vue';
import PdfViewer from '@/js/components/widgets/PdfViewer.vue';
import { onMounted, ref, computed, useTemplateRef } from 'vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Select from '@/js/components/controls/Select.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Button from '@/js/components/controls/Button.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import TaxRateSelector from '@/js/components/controls/TaxRateSelector.vue';
import PurchaseOrderSelector from '@/js/components/controls/PurchaseOrderSelector.vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import VueFlatPickr from 'vue-flatpickr-component';
import DataTable from '@/js/components/tables/DataTable.vue';
import { formatCurrency } from '@/js/utils.js';
import Selector from '@/js/components/controls/Selector.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import { confirm } from '@/js/composables/useModals.ts';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';

const drawer = useTemplateRef('drawer');
const doc = ref({
    aiSupplierInvoice: {
        line_items: [],
    },
});

const isLoading = ref(false);
const isUpdating = ref(false);
const store = useDataStore();
const selectedPurchaseOrderId = ref([]);
const purchaseOrderSelector = useTemplateRef('purchaseOrderSelector');
const selectedPurchaseOrders = ref([]);
const availableCurrencies = computed(() => store.get('available_currencies') ?? {});
const availableLineItems = ref([]);

onMounted(async () => {
    Eventbus.$on('editOCRInvoice', (id) => {
        openDrawer();
        loadInvoice(id);
    });
});

const isValid = computed(() => {
    return (
        selectedPurchaseOrderId.value.length > 0 &&
        doc.value.aiSupplierInvoice.event_id !== null &&
        doc.value.aiSupplierInvoice.supplier_id !== null &&
        doc.value.aiSupplierInvoice.title.trim() !== '' &&
        doc.value.aiSupplierInvoice.invoice_date !== null &&
        doc.value.aiSupplierInvoice.due_date !== null &&
        doc.value.aiSupplierInvoice.target_currency.trim() !== '' &&
        doc.value.aiSupplierInvoice.line_items.length > 0 &&
        doc.value.aiSupplierInvoice.line_items.every(
            (item) => item.purchase_order_line_item_id !== null && item.quantity > 0 && !isNaN(item.unit_price_target)
        ) &&
        overspendAmount.value <= 0
    );
});

const targetCurrency = computed(() => {
    return doc.value.aiSupplierInvoice.target_currency
        ? doc.value.aiSupplierInvoice.target_currency
        : useDataStore().get('account.currency_code');
});

const subtotal = computed(() => {
    return doc.value.aiSupplierInvoice.line_items.reduce((sum, item) => {
        return sum + item.quantity * item.unit_price_target;
    }, 0);
});

const taxAmount = computed(() => {
    return doc.value.aiSupplierInvoice.line_items.reduce((sum, item) => {
        const taxRate = store.get(`account.tax_rates.${item.tax_rate_id}`);
        const taxMultiplier = taxRate ? 1 + taxRate.rate / 100 : 1;
        return sum + item.quantity * item.unit_price_target * (taxMultiplier - 1);
    }, 0);
});

const total = computed(() => {
    return subtotal.value;
});
const totalGross = computed(() => {
    return subtotal.value + taxAmount.value;
});

const hasBeenInvoiced = computed(() => {
    const totalInvoiced = selectedPurchaseOrders.value.reduce((sum, po) => {
        return sum + parseFloat(po.total_invoiced_gross ?? 0);
    }, 0);
    return totalInvoiced > 0;
});

const overspendAmount = computed(() => {
    const totalBudget = selectedPurchaseOrders.value.reduce((sum, po) => {
        return sum + parseFloat(po.total ?? 0);
    }, 0);
    const totalAlreadyInvoiced = selectedPurchaseOrders.value.reduce((sum, po) => {
        return sum + parseFloat(po.total_invoiced_gross ?? 0);
    }, 0);
    return totalAlreadyInvoiced + total.value - totalBudget;
});

const loadInvoice = async (id) => {
    isLoading.value = true;
    try {
        const response = await axios.get(route('api.account.ocr.invoice', { document: id }));
        doc.value = response.data.data;
        ensureDraft();
        selectedPurchaseOrderId.value = doc.value.purchase_order_ids ?? [];
    } finally {
        isLoading.value = false;
    }
};

const save = async (closeAfterSave = false) => {
    ensureDraft();
    isUpdating.value = true;
    try {
        await axios.post(route('api.account.ocr.post-invoice', { document: doc.value.id }), {
            purchase_order_ids: selectedPurchaseOrderId.value,
            ...doc.value.aiSupplierInvoice,
        });
        if (closeAfterSave) {
            closeDrawer();
        }
    } finally {
        isUpdating.value = false;
    }
};

const convertToSupplierOrder = async () => {
    ensureDraft();
    isUpdating.value = true;
    await save();

    isUpdating.value = true;

    try {
        const response = await axios.post(route('api.account.ocr.convert-invoice', { document: doc.value.id }), {
            purchase_order_ids: selectedPurchaseOrderId.value,
            ...doc.value.aiSupplierInvoice,
        });
        capturePosthogEvent('ocr_invoice_published');

        closeDrawer();

        const supplierInvoice = response.data.supplier_invoice;

        const shouldOpen = await confirm({
            title: 'Invoice Created',
            message: 'Supplier invoice has been created successfully. Would you like to open it now?',
            confirmText: 'Open Invoice',
            cancelText: 'Close',
            confirmType: 'is-success',
        });

        if (shouldOpen && supplierInvoice) {
            Eventbus.$emit('viewInvoice', {
                type: 'SupplierInvoice',
                routeData: {
                    event: supplierInvoice.event_id,
                    purchaseOrder: selectedPurchaseOrderId.value[0],
                    supplierInvoice: supplierInvoice.id,
                    openPaymentDates: true,
                },
            });
        }
    } finally {
        isUpdating.value = false;
    }
};

const openDrawer = () => {
    drawer.value?.open();
};

const closeDrawer = () => {
    drawer.value?.close();
    isLoading.value = false;
};
function ensureDraft() {
    if (!doc.value.aiSupplierInvoice) {
        doc.value.aiSupplierInvoice = {};
    }

    doc.value.aiSupplierInvoice.invoice_number ??= '';
    doc.value.aiSupplierInvoice.supplier_id ??= null;
    doc.value.aiSupplierInvoice.title ??= '';
    doc.value.aiSupplierInvoice.invoice_date ??= null;
    doc.value.aiSupplierInvoice.due_date ??= null;
    doc.value.aiSupplierInvoice.target_currency ??= '';
    doc.value.aiSupplierInvoice.conversion_rate ??= 1;
    doc.value.aiSupplierInvoice.line_items ??= [];
}

const addLineItem = () => {
    ensureDraft();
    doc.value.aiSupplierInvoice.line_items.push({
        id: null,
        purchase_order_line_item_id: null,
        description: '',
        quantity: 1,
        unit_price_target: 0,
        tax_rate_id: null,
    });
};

const removeLineItem = (index) => {
    if (!doc.value.aiSupplierInvoice?.line_items) {
        return;
    }

    doc.value.aiSupplierInvoice.line_items.splice(index, 1);
};

function prepopulateFieldsFromPOSelection(purchaseOrders = [], { isHydrating = false } = {}) {
    selectedPurchaseOrders.value = purchaseOrders;
    if (!selectedPurchaseOrders.value || !selectedPurchaseOrders.value.length) {
        availableLineItems.value = [];
        return;
    }

    availableLineItems.value = selectedPurchaseOrders.value.flatMap((po) => po.line_items ?? []);

    if (isHydrating) {
        return;
    }

    const purchaseOrder = selectedPurchaseOrders.value[0];
    doc.value.aiSupplierInvoice.purchase_order_ids = selectedPurchaseOrders.value.map((po) => po.id);
    doc.value.aiSupplierInvoice.event_id ??= purchaseOrder.event_id;
    doc.value.aiSupplierInvoice.supplier_id ??= purchaseOrder.supplier_id;
    doc.value.aiSupplierInvoice.base_currency ??= purchaseOrder.base_currency;
    doc.value.aiSupplierInvoice.target_currency ??= purchaseOrder.target_currency;
    doc.value.aiSupplierInvoice.title = purchaseOrder.title;
    doc.value.aiSupplierInvoice.line_items = doc.value.aiSupplierInvoice.line_items.map((lineItem) => {
        lineItem.description = '';
        lineItem.purchase_order_line_item_id = null;
        return lineItem;
    });
}

function prepopulateFieldsFromPoliSelection(purchaseOrderLineItem) {
    if (!availableLineItems.value) {
        return;
    }
    const selectedPoli = availableLineItems.value.find(
        (item) => item.id === purchaseOrderLineItem.purchase_order_line_item_id
    );
    if (!selectedPoli) {
        return;
    }

    purchaseOrderLineItem.description = selectedPoli.description;
    purchaseOrderLineItem.tax_rate_id = selectedPoli.tax_rate_id;
}

function openCurrencyConverter() {
    Eventbus.$emit('currencyConverter');
}
</script>
<style lang="scss" scoped>
.pdf-view {
    width: calc(100vw - min(750px, 100vw));
    height: 100%;
    box-sizing: border-box;
    padding: 20px;
}
</style>
